import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../components/Button';
import Callout from '../../components/Callout';
import Stepper from '../../components/Stepper';
import { WIZARD_STEPS, WizardStatus } from '../../components/WizardShell';
import { PICKER, RENDER_DISCLAIMER } from '../../data/style-library';
import { getVertical } from '../../data/verticals';
import { api, type StyleImage } from '../../lib/api';
import { cx } from '../../lib/cx';

const SAVE_DEBOUNCE_MS = 700;

function Lightbox({
  image,
  index,
  onClose,
}: {
  image: StyleImage;
  index: number | null;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Enlarged photo"
      className="fixed inset-0 z-50 flex flex-col bg-ink/95 p-4 sm:p-8"
      onClick={onClose}
    >
      <div className="flex justify-end">
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          className="min-h-tap rounded-full border border-white/35 px-6 text-[0.95rem] font-semibold text-white hover:bg-white/10"
        >
          Close
        </button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center py-4">
        <img
          src={image.url}
          alt={image.caption ?? 'Building photograph'}
          className="max-h-full max-w-full object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </div>
      <div className="text-center">
        {index !== null && (
          <p className="t-meta font-semibold text-green-400">Your pick number {index + 1}</p>
        )}
        {image.caption && <p className="t-body mt-1 text-grey-300">{image.caption}</p>}
        <p className="t-meta mt-2 text-grey-400">{image.credit}</p>
      </div>
    </div>
  );
}

export default function StylePicker() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [images, setImages] = useState<StyleImage[] | null>(null);
  const [verticalId, setVerticalId] = useState<string | null>(null);
  const [picks, setPicks] = useState<string[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<StyleImage | null>(null);
  const [busy, setBusy] = useState(false);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef<string[]>([]);
  latest.current = picks;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    Promise.all([api.getProject(id), api.getStyles(id)])
      .then(([project, styles]) => {
        if (cancelled) return;
        setVerticalId(project.verticalId);
        setImages(styles.images);
        setPicks(styles.selected);
      })
      .catch(() => {
        if (!cancelled) setLoadError('We could not load the photos. Try again in a moment.');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const save = useCallback(
    (next: string[]) => {
      if (!id) return;
      void api.saveSelections(id, next).catch(() => {
        /* The next toggle retries; picks stay in the UI meanwhile. */
      });
    },
    [id],
  );

  const toggle = (imageId: string) => {
    const current = latest.current;
    let next: string[];
    if (current.includes(imageId)) next = current.filter((x) => x !== imageId);
    else if (current.length >= PICKER.max) return;
    else next = [...current, imageId];

    latest.current = next;
    setPicks(next);

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => save(next), SAVE_DEBOUNCE_MS);
  };

  // Flush on unmount so a fast tap-then-navigate never loses the last pick.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        save(latest.current);
      }
    },
    [save],
  );

  const onContinue = async () => {
    if (!id || picks.length < PICKER.min) return;
    setBusy(true);
    if (timer.current) clearTimeout(timer.current);
    try {
      await api.saveSelections(id, picks);
      await api.updateProject(id, { status: 'results' });
      navigate(`/project/${id}/results`);
    } catch {
      setBusy(false);
      setLoadError('We could not save your picks. Check your connection and try again.');
    }
  };

  if (loadError && !images) return <WizardStatus message={loadError} />;
  if (!images) return <WizardStatus message="Loading photos…" />;

  const vertical = verticalId ? getVertical(verticalId) : undefined;
  const short = PICKER.min - picks.length;
  const atMax = picks.length >= PICKER.max;

  return (
    <div className="flex min-h-dvh flex-col bg-white">
      <header className="border-b border-grey-200 px-gutter py-5">
        <div className="mx-auto w-full max-w-wrap">
          <Stepper steps={WIZARD_STEPS} current={4} />
          <div className="mt-6">
            {vertical && <p className="t-eyebrow text-green-600">{vertical.label}</p>}
            <h1 className="t-h1 mt-2 text-ink">Pick the buildings you like</h1>
            <p className="t-body-lg measure mt-3 text-grey-600">
              Choose {PICKER.min} to {PICKER.max}. Go on instinct — you are not picking
              features, just the ones that catch your eye.
            </p>
          </div>
        </div>
      </header>

      {images.length === 0 ? (
        <main className="mx-auto w-full max-w-wrap flex-1 px-gutter py-12">
          <Callout title="No photos yet for this building type">
            The photo library for {vertical?.label.toLowerCase() ?? 'this building type'} is
            still being built. Your answers are saved — we will email you when the picker is
            ready, or you can go back and choose a different building type.
          </Callout>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button variant="secondary" onClick={() => navigate(`/project/${id}/system`)}>
              Go back
            </Button>
          </div>
        </main>
      ) : (
        <>
          {/* Edge to edge: the grid is the product, so chrome stays out of it. */}
          <main className="flex-1 pb-32">
            <ul className="gap-1 px-1 pt-1 [column-count:2] sm:[column-count:3] lg:[column-count:4] [&>li]:mb-1 [&>li]:break-inside-avoid">
              {images.map((img) => {
                const rank = picks.indexOf(img.id);
                const selected = rank >= 0;
                return (
                  <li key={img.id} className="relative">
                    <button
                      type="button"
                      aria-pressed={selected}
                      aria-label={img.caption ?? 'Building photograph'}
                      disabled={!selected && atMax}
                      onClick={() => toggle(img.id)}
                      className={cx(
                        'block w-full cursor-pointer disabled:cursor-not-allowed',
                        !selected && atMax && 'opacity-45',
                      )}
                    >
                      <img
                        src={img.url}
                        alt=""
                        loading="lazy"
                        decoding="async"
                        width={img.width ?? undefined}
                        height={img.height ?? undefined}
                        // The intrinsic ratio holds the tile open before the
                        // image arrives; without it the columns collapse.
                        style={
                          img.width && img.height
                            ? { aspectRatio: `${img.width} / ${img.height}` }
                            : { aspectRatio: '4 / 3' }
                        }
                        className="block h-auto w-full bg-grey-100 object-cover"
                      />
                      {/* Selection is an inset rule and a number. Nothing else:
                          no scale, no shadow, no checkmark. */}
                      {selected && (
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-0 shadow-[inset_0_0_0_4px_var(--color-green-600)]"
                        />
                      )}
                    </button>

                    {selected && (
                      <span
                        aria-hidden="true"
                        className="t-numeral pointer-events-none absolute top-2 left-2 flex h-9 w-9 items-center justify-center rounded-full bg-green-600 text-base text-white"
                      >
                        {rank + 1}
                      </span>
                    )}

                    <button
                      type="button"
                      onClick={() => setLightbox(img)}
                      aria-label="See this photo larger"
                      className="absolute right-2 bottom-2 flex h-11 w-11 items-center justify-center rounded-full bg-ink/70 text-white hover:bg-ink"
                    >
                      <svg viewBox="0 0 20 20" className="h-5 w-5 fill-current" aria-hidden="true">
                        <path d="M4 2h5v2H6.4l4.3 4.3-1.4 1.4L5 5.4V8H3V3a1 1 0 0 1 1-1Zm12 16h-5v-2h2.6l-4.3-4.3 1.4-1.4 4.3 4.3V12h2v5a1 1 0 0 1-1 1Z" />
                      </svg>
                    </button>
                  </li>
                );
              })}
            </ul>

            <p className="t-meta px-gutter py-8 text-grey-600">{RENDER_DISCLAIMER}</p>
          </main>

          {/* Persistent, because on a phone the count is the whole game. */}
          <div className="fixed inset-x-0 bottom-0 z-40 border-t border-grey-200 bg-white/95 backdrop-blur-[14px]">
            <div className="mx-auto flex w-full max-w-wrap flex-wrap items-center justify-between gap-3 px-gutter py-4">
              <p className="t-body text-ink" aria-live="polite">
                {picks.length === 0 ? (
                  <>Pick at least {PICKER.min}</>
                ) : short > 0 ? (
                  <>
                    <span className="t-numeral font-semibold">{picks.length}</span> of{' '}
                    <span className="t-numeral">{PICKER.min}</span> minimum
                  </>
                ) : (
                  <>
                    <span className="t-numeral font-semibold">{picks.length}</span> selected
                    {atMax && <span className="text-grey-600"> — that is the maximum</span>}
                  </>
                )}
              </p>
              <Button
                onClick={() => void onContinue()}
                disabled={picks.length < PICKER.min || busy}

              >
                {busy ? 'Saving…' : 'See my concepts'}
              </Button>
            </div>
          </div>
        </>
      )}

      {lightbox && (
        <Lightbox
          image={lightbox}
          index={picks.indexOf(lightbox.id) >= 0 ? picks.indexOf(lightbox.id) : null}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
